import React, { useState } from "react";
import {
    CrownSimple, WarningCircle, Trophy, Shield, ArrowFatDown, Info, CaretDown, Newspaper,
} from "@phosphor-icons/react";
import { slugify } from "../lib/slug";

// ---- Status pill styling --------------------------------------------------

const STATUS_STYLE = {
    // Title race
    CHAMPION: { bg: "#9EE8B4", fg: "#050505", label: "CHAMPION" },
    FAVOURITE: { bg: "#9EE8B4", fg: "#050505", label: "FAVOURITE" },
    IN_RACE: { bg: "#FFF3C2", fg: "#050505", label: "IN RACE" },
    NEEDS_HELP: { bg: "#FFD180", fg: "#050505", label: "NEEDS HELP" },
    // Europe race
    QUALIFIED: { bg: "#9EE8B4", fg: "#050505", label: "QUALIFIED" },
    LIKELY: { bg: "#9DB7F5", fg: "#050505", label: "LIKELY" },
    IN_THE_RACE: { bg: "#FFF3C2", fg: "#050505", label: "IN RACE" },
    DANGER: { bg: "#FF8800", fg: "#050505", label: "DANGER" },
    MUST_WIN: { bg: "#FF6B6B", fg: "#ffffff", label: "MUST WIN" },
    ELIMINATED: { bg: "#A1A1AA", fg: "#050505", label: "OUT" },
    OUT: { bg: "#A1A1AA", fg: "#050505", label: "OUT" },
    // Relegation
    SAFE: { bg: "#9EE8B4", fg: "#050505", label: "SAFE" },
    LIKELY_SAFE: { bg: "#A6E5FF", fg: "#050505", label: "LIKELY SAFE" },
    AT_RISK: { bg: "#FFD180", fg: "#050505", label: "AT RISK" },
    LIKELY_RELEGATED: { bg: "#FF6B6B", fg: "#ffffff", label: "LIKELY RELEGATED" },
    RELEGATED: { bg: "#FF2A2A", fg: "#ffffff", label: "RELEGATED" },
};

const TIER_STYLE = {
    CL_LOCKED: { fg: "#9EE8B4", label: "CL LOCKED" },
    CL_RACE: { fg: "#9DB7F5", label: "CL RACE" },
    EL_LOCKED: { fg: "#FFD600", label: "EL LOCKED" },
    EL_RACE: { fg: "#FFF3C2", label: "EL RACE" },
    ECL_LOCKED: { fg: "#A6E5FF", label: "ECL LOCKED" },
    ECL_RACE: { fg: "#9DB7F5", label: "ECL RACE" },
    EUROPE_LOCKED: { fg: "#9EE8B4", label: "EUROPE LOCKED" },
    EUROPE_RACE: { fg: "#FFF3C2", label: "EUROPE RACE" },
    OUT_OF_RACE: { fg: "#A1A1AA", label: "OUT OF RACE" },
};

function StatusPill({ tag, testid }) {
    const style = STATUS_STYLE[tag] || STATUS_STYLE.IN_THE_RACE;
    return (
        <span
            className="font-tag text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 leading-none"
            style={{ background: style.bg, color: style.fg }}
            data-testid={testid}
        >
            {style.label}
        </span>
    );
}

function TierTag({ tier }) {
    if (!tier) return null;
    const s = TIER_STYLE[tier] || TIER_STYLE.OUT_OF_RACE;
    return (
        <span
            className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 leading-none border"
            style={{ color: s.fg, borderColor: `${s.fg}55` }}
        >
            {s.label}
        </span>
    );
}

function NoteBadge({ note }) {
    const map = {
        MUST_WIN: { fg: "#FF2A2A" },
        LOCKED: { fg: "#9EE8B4" },
        OUT: { fg: "#A1A1AA" },
        AT_RISK: { fg: "#FF8800" },
        RELEGATED: { fg: "#FF2A2A" },
    };
    const s = map[note] || { fg: "#FFFFFF" };
    return (
        <span
            className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 leading-none"
            style={{ color: s.fg, border: `1px solid ${s.fg}55` }}
        >
            {note.replace("_", " ")}
        </span>
    );
}

// ---- Expandable detail panel (brief Section 4) --------------------------

function FdrBadge({ fdr }) {
    if (!fdr) return null;
    const colors = { 1: "#9EE8B4", 2: "#BFF29E", 3: "#FFD600", 4: "#FF8800", 5: "#FF2A2A" };
    const bg = colors[fdr] || "#A1A1AA";
    return (
        <span
            className="font-mono text-[9px] font-bold px-1 py-0.5"
            style={{ background: `${bg}20`, color: bg, border: `1px solid ${bg}60` }}
            title={`FDR ${fdr}`}
        >
            {fdr}
        </span>
    );
}

function FantasyRelevance({ row, kind }) {
    // Derive a quick fantasy-relevance hint from the status + notes fields we already
    // ship on the qualification row. Mirrors `qualification_modifiers.get_modifier`
    // messaging so UX matches the downstream prediction adjustment.
    const bullets = [];
    if (row.status_tag === "MUST_WIN") {
        bullets.push("Attackers boosted +8% — scoreline desperation favours set-piece/penalty takers.");
    }
    if (kind === "relegation" && row.status_tag === "DANGER") {
        bullets.push("Talisman + penalty takers carry elevated upside this window.");
    }
    if (["QUALIFIED", "CHAMPION", "SAFE"].includes(row.status_tag)
        || ["CL_LOCKED", "EUROPE_LOCKED"].includes(row.europe_tier_status)) {
        bullets.push("Rotation risk elevated — expect minutes capped at ~85% of baseline.");
    }
    if (row.notes?.includes("AT_RISK")) {
        bullets.push("Defenders risky — likely under pressure from chasing teams.");
    }
    if (bullets.length === 0) {
        bullets.push("No qualification-driven modifier — fantasy model uses baseline projections.");
    }
    return (
        <ul className="list-disc list-inside space-y-1 font-mono text-[11px] text-white/70 leading-snug">
            {bullets.map((b) => <li key={b}>{b}</li>)}
        </ul>
    );
}

function ExpandedPanel({ row, kind }) {
    return (
        <div
            className="bg-black/50 border-l-2 border-fa-cream/40 pl-4 pr-3 py-3 mx-1 mb-1 grid grid-cols-1 md:grid-cols-3 gap-4"
            data-testid={`expanded-${kind}-${slugify(row.team_name)}`}
        >
            {/* Column 1 — probabilities + explanation */}
            <div className="space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-1">
                    Model insight
                </div>
                <div className="font-mono text-[11px] text-white/80 leading-snug">
                    {kind === "europe" && row.qualification_explanation}
                    {kind === "relegation" && row.survival_explanation}
                    {kind === "title" && row.title_explanation}
                </div>
                <div className="font-mono text-[10px] text-white/60 leading-relaxed">
                    <div>Projected: <span className="text-[#9DB7F5]">{row.projected_points} pts</span></div>
                    <div>Max possible: <span className="text-white/75">{row.max_possible_points} pts</span></div>
                    {kind === "europe" && (
                        <div>
                            Guaranteed tier: <span className="text-fa-cream">{row.guaranteed_minimum_tier}</span>
                            <span className="text-white/30 mx-1">·</span>
                            Best: <span className="text-fa-yellow">{row.best_possible_tier}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Column 2 — upcoming fixtures */}
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2">
                    Key upcoming fixtures
                </div>
                {row.key_upcoming_fixtures?.length ? (
                    <div className="space-y-1">
                        {row.key_upcoming_fixtures.map((f) => (
                            <div
                                key={`${f.gw}-${f.opponent_id}`}
                                className="flex items-center gap-2 bg-black/40 border border-white/5 px-2 py-1 font-mono text-[11px]"
                                data-testid={`expanded-fx-${slugify(row.team_name)}-${f.gw}`}
                            >
                                <span className="text-white/40 w-10">GW{f.gw}</span>
                                <span className="text-white/40">{f.home_away}</span>
                                <span className="text-white flex-1 truncate">
                                    {f.opponent_short || f.opponent_name}
                                </span>
                                <FdrBadge fdr={f.fdr} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="font-mono text-[10px] text-white/40 italic">
                        Fixture feed unavailable — check back after FPL maintenance window.
                    </div>
                )}
            </div>

            {/* Column 3 — news + fantasy relevance */}
            <div className="space-y-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2 flex items-center gap-1">
                        <Newspaper size={11} weight="fill" />
                        News signals
                    </div>
                    {row.news_signals?.length ? (
                        <div className="space-y-1">
                            {row.news_signals.slice(0, 3).map((n) => (
                                <div
                                    key={n.id}
                                    className="bg-black/40 border border-white/5 px-2 py-1.5 font-mono text-[10px] leading-snug"
                                    data-testid={`expanded-news-${slugify(row.team_name)}-${n.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-fa-cream uppercase tracking-widest text-[9px]">
                                            {n.topic.replace("_", " ")}
                                        </span>
                                        <span className="text-white/40 text-[9px]">{n.source}</span>
                                    </div>
                                    <div className="text-white/85 mt-0.5">{n.headline}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="font-mono text-[10px] text-white/40 italic">
                            No relevant news signals.
                        </div>
                    )}
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2">
                        Fantasy relevance
                    </div>
                    <FantasyRelevance row={row} kind={kind} />
                </div>
            </div>
        </div>
    );
}

function ExpandToggle({ open, onToggle, kind, slug }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            data-testid={`${kind}-expand-${slug}`}
            className={`p-1 text-white/40 hover:text-fa-cream transition-transform ${open ? "rotate-180 text-fa-cream" : ""}`}
            title={open ? "Collapse" : "Expand details"}
        >
            <CaretDown size={12} weight="bold" />
        </button>
    );
}

// ---- Cards ----------------------------------------------------------------

function HelperLine({ children }) {
    return (
        <div className="flex items-start gap-2 mb-3 font-mono text-[11px] text-white/50 leading-snug">
            <Info size={12} weight="bold" className="mt-0.5 text-white/40 shrink-0" />
            <span>{children}</span>
        </div>
    );
}

function CardHeader({ icon, color, title, columns, grid }) {
    const gridClass = grid || "grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr]";
    return (
        <>
            <div
                className="font-mono text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1.5"
                style={{ color }}
            >
                {icon}
                {title}
            </div>
            <div className={`grid ${gridClass} gap-2 px-1 py-1 font-mono text-[9px] uppercase tracking-widest text-white/35 border-b border-white/5`}>
                {columns.map((c) => (
                    <div key={c} className={c === "Status" || c.includes("→") ? "text-right" : ""}>{c}</div>
                ))}
            </div>
        </>
    );
}

// ---- Title race -----------------------------------------------------------

export function TitleRaceCard({ rows, asOfGameweek, className = "" }) {
    const [expanded, setExpanded] = useState(null);
    return (
        <div className={`bg-[#121212] border border-white/10 p-4 ${className}`} data-testid="title-race-card">
            <CardHeader
                icon={<CrownSimple size={14} weight="fill" />}
                color="#FFF3C2"
                title={`Title Race · GW${asOfGameweek}`}
                columns={["#", "Team", "Pts (GD)", "Projected", "Max", "Status", "Title %", "Note", ""]}
                grid="grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px]"
            />
            <HelperLine>
                Title probabilities come from a 4 000-run Monte-Carlo simulation of the remaining
                fixtures. "Projected" extrapolates current points-per-game; "Max" assumes wins
                from here on out. Click any row for the full breakdown.
            </HelperLine>
            {rows.length === 0 ? (
                <div className="text-white/30 text-xs italic">No data</div>
            ) : (
                <div className="space-y-1">
                    {rows.map((r) => {
                        const key = r.team_id || r.team_name;
                        const isOpen = expanded === key;
                        const slug = slugify(r.team_name);
                        return (
                            <React.Fragment key={key}>
                                <div
                                    className={`grid grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px] gap-2 items-center px-1 py-1.5 border-b border-white/5 cursor-pointer hover:bg-white/[0.02] ${isOpen ? "bg-white/[0.03]" : ""}`}
                                    data-testid={`title-row-${slug}`}
                                    onClick={() => setExpanded(isOpen ? null : key)}
                                >
                                    <span className="font-mono text-[10px] text-white/40">{String(r.position).padStart(2, "0")}</span>
                                    <span className="font-display font-bold uppercase text-sm truncate">{r.team_name}</span>
                                    <span className="font-mono text-[11px] text-white/70 tabular-nums">
                                        {r.points} <span className="text-white/35">({r.goal_difference >= 0 ? "+" : ""}{r.goal_difference})</span>
                                    </span>
                                    <span className="font-mono text-[10px] tabular-nums" style={{ color: "#9DB7F5" }}>
                                        {r.projected_points} pts
                                    </span>
                                    <span className="font-mono text-[10px] tabular-nums text-white/50">
                                        {r.max_possible_points} pts
                                    </span>
                                    <span><StatusPill tag={r.status_tag} testid={`title-status-${slug}`} /></span>
                                    <span className="font-mono text-[11px] font-bold tabular-nums text-fa-yellow text-right">
                                        {r.probability_to_win_title.toFixed(1)}%
                                    </span>
                                    <span
                                        className="font-mono text-[10px] text-white/55 leading-snug truncate"
                                        title={r.title_explanation}
                                        data-testid={`title-note-${slug}`}
                                    >
                                        {r.title_explanation || "—"}
                                    </span>
                                    <ExpandToggle open={isOpen} onToggle={() => {}} kind="title" slug={slug} />
                                </div>
                                {isOpen && <ExpandedPanel row={r} kind="title" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---- Europe race ----------------------------------------------------------

export function EuropeRaceCard({ rows, cutoffPoints, asOfGameweek, rules, className = "" }) {
    const [expanded, setExpanded] = useState(null);
    return (
        <div className={`bg-[#121212] border border-white/10 p-4 ${className}`} data-testid="europe-race-card">
            <CardHeader
                icon={<Trophy size={14} weight="fill" />}
                color="#9DB7F5"
                title={`Europe Race · GW${asOfGameweek} · cutoff ${cutoffPoints} pts`}
                columns={["#", "Team", "Pts (GD)", "Gap", "Proj/Max", "Status", "CL · EL · ECL · Any", "Note", ""]}
                grid="grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px]"
            />
            <HelperLine>
                This table shows which teams are still alive in the race for European qualification.
                Probabilities account for current standings, points-per-game projections and the
                {" "}<strong className="text-white/70">{rules?.season || "current"}</strong> EPL slot allocation
                ({rules?.champions_league_slots || 4} CL · {rules?.europa_league_slots || 1} EL · {rules?.conference_league_slots || 1} ECL).
                Click a row for the full breakdown.
            </HelperLine>
            <div className="space-y-1">
                {rows.map((r) => {
                    const key = r.team_id || r.team_name;
                    const isOpen = expanded === key;
                    return (
                        <React.Fragment key={key}>
                            <EuropeRow
                                r={r}
                                cutoff={cutoffPoints}
                                expanded={isOpen}
                                onToggle={() => setExpanded(isOpen ? null : key)}
                            />
                            {isOpen && <ExpandedPanel row={r} kind="europe" />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

function EuropeRow({ r, cutoff, expanded, onToggle }) {
    const slug = slugify(r.team_name);
    const gapLabel = r.in_qualification
        ? `${r.cushion} pts above cutoff`
        : `${r.gap_to_target} pts behind cutoff`;
    const gapColor = r.in_qualification ? "#9EE8B4" : "#FF6B6B";
    return (
        <div
            className={`grid grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px] gap-2 items-center px-1 py-1.5 border-b border-white/5 cursor-pointer hover:bg-white/[0.02] ${expanded ? "bg-white/[0.03]" : ""}`}
            data-testid={`europe-row-${slug}`}
            onClick={onToggle}
            title={`Projected ${r.projected_points} pts · max possible ${r.max_possible_points} pts · ${gapLabel}`}
        >
            <span className="font-mono text-[10px] text-white/40">{String(r.position).padStart(2, "0")}</span>
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-display font-bold uppercase text-sm truncate flex items-center gap-1.5">
                    {r.team_name}
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                    <TierTag tier={r.europe_tier_status} />
                    {(r.notes || []).map((n) => <NoteBadge key={n} note={n} />)}
                    {r.news_signals?.length > 0 && (
                        <span
                            className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 leading-none text-fa-yellow border border-fa-yellow/40"
                            title={`${r.news_signals.length} news signal${r.news_signals.length > 1 ? "s" : ""}`}
                        >
                            NEWS · {r.news_signals.length}
                        </span>
                    )}
                </div>
            </div>
            <span className="font-mono text-[11px] text-white/70 tabular-nums">
                {r.points} <span className="text-white/35">({r.goal_difference >= 0 ? "+" : ""}{r.goal_difference})</span>
            </span>
            <span
                className="font-mono text-[10px] tabular-nums font-bold leading-tight"
                style={{ color: gapColor }}
                data-testid={`europe-gap-${slug}`}
            >
                {r.in_qualification ? `+${r.cushion}` : `−${r.gap_to_target}`}
                <span className="block text-[8px] uppercase tracking-widest font-normal text-white/40 mt-0.5">
                    {r.in_qualification ? "above cutoff" : "to Europe"}
                </span>
            </span>
            <span className="font-mono text-[9px] tabular-nums text-white/55 leading-tight flex flex-col">
                <span>Proj <span className="text-[#9DB7F5]">{r.projected_points}</span></span>
                <span className="text-white/40">Max {r.max_possible_points}</span>
            </span>
            <span><StatusPill tag={r.status_tag} testid={`europe-status-${slug}`} /></span>
            <span className="font-mono text-[10px] tabular-nums flex flex-col items-end gap-0.5">
                <span className="text-white/85">
                    <span style={{ color: "#9EE8B4" }}>CL {r.probability_champions_league.toFixed(0)}%</span>
                    <span className="text-white/30 mx-1">·</span>
                    <span style={{ color: "#FFD600" }}>EL {r.probability_europa_league.toFixed(0)}%</span>
                    <span className="text-white/30 mx-1">·</span>
                    <span style={{ color: "#A6E5FF" }}>ECL {r.probability_conference_league.toFixed(0)}%</span>
                </span>
                <span className="text-fa-yellow font-bold" data-testid={`europe-any-${slug}`}>
                    Any Europe {r.probability_any_europe.toFixed(0)}%
                </span>
            </span>
            <span
                className="font-mono text-[10px] text-white/55 leading-snug line-clamp-2"
                title={r.qualification_explanation}
                data-testid={`europe-note-${slug}`}
            >
                {r.qualification_explanation || "—"}
            </span>
            <ExpandToggle open={expanded} onToggle={() => {}} kind="europe" slug={slug} />
        </div>
    );
}

// ---- Relegation -----------------------------------------------------------

export function RelegationCard({ rows, cutoffPoints, asOfGameweek, className = "" }) {
    const [expanded, setExpanded] = useState(null);
    return (
        <div className={`bg-[#121212] border border-white/10 p-4 ${className}`} data-testid="relegation-card">
            <CardHeader
                icon={<WarningCircle size={14} weight="fill" />}
                color="#FF6B6B"
                title={`Relegation Battle · GW${asOfGameweek} · 18th place ${cutoffPoints} pts`}
                columns={["#", "Team", "Pts (GD)", "Safety", "Proj/Max", "Status", "Survive · Relegate", "Note", ""]}
                grid="grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px]"
            />
            <HelperLine>
                Estimates each team&apos;s survival chances using current points, projected
                points-per-game and a 4 000-run remaining-fixture simulation. Bottom three are
                relegated. Teams already mathematically safe are hidden once everyone above
                them is also out of the race. Click any row for the full breakdown.
            </HelperLine>
            <div className="space-y-1">
                {rows.map((r) => {
                    const key = r.team_id || r.team_name;
                    const isOpen = expanded === key;
                    return (
                        <React.Fragment key={key}>
                            <RelegationRow
                                r={r}
                                expanded={isOpen}
                                onToggle={() => setExpanded(isOpen ? null : key)}
                            />
                            {isOpen && <ExpandedPanel row={r} kind="relegation" />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

function RelegationRow({ r, expanded, onToggle }) {
    const slug = slugify(r.team_name);
    const above = r.gap_to_safety <= 0;
    return (
        <div
            className={`grid grid-cols-[28px_1.4fr_60px_70px_70px_1fr_auto_1.4fr_18px] gap-2 items-center px-1 py-1.5 border-b border-white/5 cursor-pointer hover:bg-white/[0.02] ${expanded ? "bg-white/[0.03]" : ""}`}
            data-testid={`relegation-row-${slug}`}
            onClick={onToggle}
            title={
                above
                    ? `${Math.abs(r.gap_to_safety)} pts above the relegation safety line (4th-from-bottom)`
                    : `Needs ${r.gap_to_safety} pts to climb out of the relegation zone`
            }
        >
            <span className="font-mono text-[10px] text-white/40">{String(r.position).padStart(2, "0")}</span>
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-display font-bold uppercase text-sm truncate">{r.team_name}</span>
                <div className="flex items-center gap-1 flex-wrap">
                    {(r.notes || []).map((n) => <NoteBadge key={n} note={n} />)}
                </div>
            </div>
            <span className="font-mono text-[11px] text-white/70 tabular-nums">
                {r.points} <span className="text-white/35">({r.goal_difference >= 0 ? "+" : ""}{r.goal_difference})</span>
            </span>
            <span
                className="font-mono text-[10px] tabular-nums font-bold leading-tight"
                style={{ color: above ? "#9EE8B4" : "#FF6B6B" }}
            >
                {above ? `+${Math.abs(r.gap_to_safety)}` : `−${r.gap_to_safety}`}
                <span className="block text-[8px] uppercase tracking-widest font-normal text-white/40 mt-0.5">
                    {above ? "above drop" : "to safety"}
                </span>
            </span>
            <span className="font-mono text-[9px] tabular-nums text-white/55 leading-tight flex flex-col">
                <span>Proj <span className="text-[#9DB7F5]">{r.projected_points}</span></span>
                <span className="text-white/40">Max {r.max_possible_points}</span>
            </span>
            <span><StatusPill tag={r.status_tag} testid={`relegation-status-${slug}`} /></span>
            <span className="font-mono text-[10px] tabular-nums flex flex-col items-end">
                <span style={{ color: "#9EE8B4" }}>Survive {r.probability_to_survive.toFixed(0)}%</span>
                <span style={{ color: "#FF6B6B" }} data-testid={`relegation-prob-${slug}`}>
                    Relegate {r.probability_to_relegate.toFixed(0)}%
                </span>
            </span>
            <span
                className="font-mono text-[10px] text-white/55 leading-snug line-clamp-2"
                title={r.survival_explanation}
                data-testid={`relegation-note-${slug}`}
            >
                {r.survival_explanation || "—"}
            </span>
            <ExpandToggle open={expanded} onToggle={() => {}} kind="relegation" slug={slug} />
        </div>
    );
}
